import React, { ChangeEventHandler, FormEventHandler, useState } from 'react'
import { ReactFC } from 'common/types/ReactFC'
import InputGroup from 'components/forms/InputGroup'
import Button from 'components/forms/Button'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { preventDefault } from 'common/utils/preventDefault'
import Strings from 'project/localisation'
import { marketplaceStrings } from 'common/strings/marketplace-strings'
import dataRelay from 'data-relay'

type BecomeAPartnerFormType = {}

export const formSchema = {
  company_logo: yup.string().nullable(),
  company_name: yup.string().required('Required'),
  company_website: yup.string().required('Required'),
  contact_email: yup
    .string()
    .email('Please provide a valid email address')
    .required('Required'),
  contact_first_name: yup.string().required('Required'),
  contact_last_name: yup.string().required('Required'),
  contact_phone: yup.string().nullable(),
  listing_description: yup.string().required('Required'),
  listing_name: yup.string().required('Required'),
  listing_redemption: yup.string().required('Required'),
}
const schema = yup.object().shape(formSchema)

export const formInitialValues = {
  company_logo: '',
  company_name: '',
  company_website: '',
  contact_email: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_phone: '',
  listing_description: '',
  listing_name: '',
  listing_redemption: ``,
}

const BecomeAPartnerForm: ReactFC<BecomeAPartnerFormType> = ({}) => {
  const [allTouched, setAllTouched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setTouched,
    setValues,
    touched,
    values,
  } = useFormik({
    initialValues: {
      ...formInitialValues,
    },
    onSubmit: async () => {
      if (isLoading) {
        return
      }
      console.log(values)
      const v = {}
      Object.keys(values).map((k) => {
        if (values[k]) {
          v[k.replace(/_/g, ' ')] = values[k].replace(/\n/g, '<br/>')
        }
      })
      setIsLoading(true)
      setSuccess(false)
      try {
        await dataRelay.sendEvent(v, { apiKey: 'efb8-4aec-341b' })
        await setValues(formInitialValues)
        setSuccess(true)
      } catch (e) {}
      setIsLoading(false)
    },
    validateOnMount: true,
    validationSchema: () => schema,
  })

  const validate = () => {
    setAllTouched(true)
    const newTouched = {}
    Object.keys(errors).map((v) => {
      // @ts-ignore
      newTouched[v] = true
    })
    setTouched(newTouched)
    if (Object.keys(errors).length) {
      const errorEl = Object.keys(errors)[0]
      let element = document.querySelector(`[for=${errorEl}`)
      if (!element) {
        element = document.querySelector(`#${errorEl}`)
      }
      // @ts-ignore
      const y = element.getBoundingClientRect().top + window.pageYOffset
      // @ts-ignore
      window.scrollTo({ behavior: 'smooth', top: y })
      return false
    }
    return true
  }
  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    preventDefault(e)
    validate()
    handleSubmit(e)
  }

  const changeHandler: ChangeEventHandler = (e) => {
    handleChange(e)
  }

  return (
    <>
      {!success && (
        <form id={'partner-form'} onSubmit={onSubmit}>
          <h6>{marketplaceStrings.listing}</h6>
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.listingName}
            required
            name='listing_name'
            id='listing_name'
            type='text'
            placeholder={marketplaceStrings.listingNamePlaceholder}
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.listing_name}
            isValid={!errors.listing_name}
            touched={allTouched}
            errorMessage={
              errors.listing_name && (touched.listing_name || allTouched)
                ? errors.listing_name
                : undefined
            }
          />
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.listingDescription}
            placeholder={marketplaceStrings.listingDescriptionPlaceholder}
            required
            name='listing_description'
            id='listing_description'
            textarea
            type='text'
            inputProps={{ maxLength: 300 }}
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.listing_description}
            isValid={!errors.listing_description}
            touched={allTouched}
            errorMessage={
              errors.listing_description &&
              (touched.listing_description || allTouched)
                ? errors.listing_description
                : undefined
            }
          />
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.listingRedemption}
            required
            name='listing_redemption'
            id='listing_redemption'
            textarea
            placeholder={marketplaceStrings.listingRedemptionDescription}
            type='text'
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.listing_redemption}
            isValid={!errors.listing_redemption}
            touched={allTouched}
            errorMessage={
              errors.listing_redemption &&
              (touched.listing_redemption || allTouched)
                ? errors.listing_redemption
                : undefined
            }
          />
          <h6>{marketplaceStrings.company}</h6>
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.companyName}
            required
            name='company_name'
            id='company_name'
            type='text'
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.company_name}
            isValid={!errors.company_name}
            touched={allTouched}
            errorMessage={
              errors.company_name && (touched.company_name || allTouched)
                ? errors.company_name
                : undefined
            }
          />
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.companyWebsite}
            name='company_website'
            id='company_website'
            type='text'
            required
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.company_website}
            isValid={!errors.company_website}
            touched={allTouched}
            errorMessage={
              errors.company_website && (touched.company_website || allTouched)
                ? errors.company_website
                : undefined
            }
          />
          <InputGroup
            className='mb-lg-3 mb-5'
            title={marketplaceStrings.companyLogo}
            name='company_logo'
            id='company_logo'
            type='text'
            onChange={changeHandler}
            onBlur={handleBlur}
            value={values.company_logo}
            isValid={!errors.company_logo}
            touched={allTouched}
            errorMessage={
              errors.company_logo && (touched.company_logo || allTouched)
                ? errors.company_logo
                : undefined
            }
          />
          <h6>{marketplaceStrings.contact}</h6>
          <div className='row'>
            <div className='col-md-6'>
              <InputGroup
                className='mb-lg-3 mb-5'
                title={Strings.firstName}
                name='contact_first_name'
                id='contact_first_name'
                type='text'
                required
                onChange={changeHandler}
                onBlur={handleBlur}
                value={values.contact_first_name}
                isValid={!errors.contact_first_name}
                touched={allTouched}
                errorMessage={
                  errors.contact_first_name &&
                  (touched.contact_first_name || allTouched)
                    ? errors.contact_first_name
                    : undefined
                }
              />
            </div>
            <div className='col-md-6'>
              <InputGroup
                className='mb-lg-3 mb-5'
                title={Strings.lastName}
                required
                name='contact_last_name'
                id='contact_last_name'
                type='text'
                onChange={changeHandler}
                onBlur={handleBlur}
                value={values.contact_last_name}
                isValid={!errors.contact_last_name}
                touched={allTouched}
                errorMessage={
                  errors.contact_last_name &&
                  (touched.contact_last_name || allTouched)
                    ? errors.contact_last_name
                    : undefined
                }
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-md-6'>
              <InputGroup
                className='mb-lg-3 mb-5'
                title={Strings.emailAddress}
                name='contact_email'
                id='contact_email'
                type='text'
                required
                onChange={changeHandler}
                onBlur={handleBlur}
                value={values.contact_email}
                isValid={!errors.contact_email}
                touched={allTouched}
                errorMessage={
                  errors.contact_email && (touched.contact_email || allTouched)
                    ? errors.contact_email
                    : undefined
                }
              />
            </div>
            <div className='col-md-6'>
              <InputGroup
                className='mb-lg-3 mb-5'
                title={Strings.phone}
                name='contact_phone'
                id='contact_phone'
                type='text'
                onChange={changeHandler}
                onBlur={handleBlur}
                value={values.contact_phone}
                isValid={!errors.contact_phone}
                touched={allTouched}
                errorMessage={
                  errors.contact_phone && (touched.contact_phone || allTouched)
                    ? errors.contact_phone
                    : undefined
                }
              />
            </div>
          </div>
          <Button
            disabled={isLoading}
            type='submit'
            theme='black'
            size={'small'}
            className={'d-block ms-auto me-lg-0 me-auto py-2 px-26'}
          >
            {marketplaceStrings.submit}
          </Button>
        </form>
      )}
      {!!success && (
        <div
          dangerouslySetInnerHTML={{
            __html: marketplaceStrings.successMessage,
          }}
        />
      )}
    </>
  )
}

export default BecomeAPartnerForm
